/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import EventEmitter from 'eventemitter3';

import SchedulePlayer from './schedule-player';

/**
 * Strategie přehrávání pro samostatné (osiřelé) médium nebo šablonu.
 * Přidává k původní strategii vlastní časovač přehrávání.
 * V jiné situaci není doba přehrávání používána.
 * @implements {ScheduleStrategy}
 */
class ScheduleOrphan {
  /**
   * Vytvoří instanci.
   * @param {number} playDuration Doba přehrávání média nebo šablony. [ms]
   * @param {ScheduleStrategy} scheduleStrategy Strategie pro přehrávání bez časovače.
   * @returns {ScheduleOrphan} Nová instance.
   */
  constructor(playDuration, scheduleStrategy) {
    const schedulePlayer = new SchedulePlayer();
    /**
     * Správce událostí.
     * @private
     * @type {EventEmitter}
     */
    this.emitter = new EventEmitter();
    this.playDuration = playDuration;
    /**
     * Aktuální informace o přehrávání.
     * @private
     * @type {?ScheduleInfo}
     */
    this.scheduleInfo = null;
    this.schedulePlayer = schedulePlayer;
    this.scheduleStrategy = scheduleStrategy;
    // Navázání naslouchání na instanci.
    this.onGone = this.onGone.bind(this);
    this.onPlayed = this.onPlayed.bind(this);
    // Naslouchání událostem.
    schedulePlayer.on('played', this.onPlayed);
    scheduleStrategy.on('gone', this.onGone);
  }

  /** @inheritdoc */
  destroy() {
    const { schedulePlayer, scheduleStrategy } = this;
    schedulePlayer.off('played', this.onPlayed);
    schedulePlayer.destroy();
    scheduleStrategy.off('gone', this.onGone);
    scheduleStrategy.destroy();
  }

  /** @inheritdoc */
  go() {
    const { schedulePlayer } = this;
    // Zastaví se případné přehrávání z předchozího cyklu.
    schedulePlayer.stop();
    // Spuštění nového přehrávání.
    schedulePlayer.play(this.playDuration);
    // Přechod na další slajd.
    return this.scheduleStrategy.go();
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na přechod na další slajd.
   * @param {ScheduleInfo} scheduleInfo Informace potřebné k přehrání slajdu, na které byl proveden přechod.
   * @private
   * @returns {undefined} Nic.
   */
  onGone(scheduleInfo) {
    this.scheduleInfo = scheduleInfo;
    this.emitter.emit('gone', scheduleInfo);
  }

  /**
   * Reakce na přehrání slajdu.
   * @private
   * @returns {undefined} Nic.
   */
  onPlayed() {
    this.emitter.emit('played', this.scheduleInfo);
  }

  /** @inheritdoc */
  plan(offset) {
    return this.scheduleStrategy.plan(offset);
  }

  /** @inheritdoc */
  skip() {
    this.scheduleStrategy.skip();
  }

  /** @inheritdoc */
  stop() {
    this.schedulePlayer.stop();
  }

  /** @inheritdoc */
  toString() {
    return `ScheduleOrphan {scheduleStrategy: ${this.scheduleStrategy}}`;
  }
}

export default ScheduleOrphan;
